import {useFieldArray, useFormContext} from 'react-hook-form';
import {
  ChecklistSidebarSelection,
  FolderValues,
  GENERAL_FOLDER_NAME,
  isGeneralChecklistFolder,
  NetworkDetailsFormValues,
  sortFoldersWithGeneralFirst,
  NetworkNodeFieldPath,
} from '../../../types';
import {SVG} from '../../../../../../../constants/assetLists';
import {useState} from 'react';
import AddFolderDialog from './AddFolderDialog';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  selectedItem: ChecklistSidebarSelection | null;
  onSelectShift: (id: string) => void;
  onSelectFolder: (id: string) => void;
  getChecklistCount: (type: 'shift' | 'folder', id: string) => number;
};

function ChecklistSidebar({
  selectedFieldPath,
  selectedItem,
  onSelectShift,
  onSelectFolder,
  getChecklistCount,
}: Props) {
  const [showAddFolderDialog, setShowAddFolderDialog] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderIndex, setSelectedFolderIndex] = useState<number | null>(
    null,
  );
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const {control, watch} = useFormContext<NetworkDetailsFormValues>();
  const {append, remove} = useFieldArray({
    control,
    name: `${selectedFieldPath}.checklistFolders`,
    keyName: 'fieldKey',
  });
  const {replace: replaceChecklist} = useFieldArray({
    control,
    name: `${selectedFieldPath}.checklists`,
    keyName: 'id',
  });

  const checklists = watch(`${selectedFieldPath}.checklists`) || [];
  const shifts = watch(`${selectedFieldPath}.shifts`) || [];
  const checklistFolders = watch(`${selectedFieldPath}.checklistFolders`) || [];

  const handleAddFolder = (name: string) => {
    append({id: crypto.randomUUID(), name});
  };

  const handleRemoveFolder = (idx: number, folder: FolderValues) => {
    if (isGeneralChecklistFolder(folder)) return;

    if (getChecklistCount('folder', folder.id) === 0) {
      remove(idx);
    } else {
      setSelectedFolderIndex(idx);
      setSelectedFolderId(folder.id);
      setShowConfirmationDialog(true);
    }
  };

  const handleCloseConfirmationDialog = () => {
    setSelectedFolderIndex(null);
    setSelectedFolderId(null);
    setShowConfirmationDialog(false);
  };

  const handleRemoveFolderChecklists = () => {
    if (!selectedFolderId || selectedFolderIndex === null) return;

    replaceChecklist(
      checklists.filter(checklist => checklist.folderId !== selectedFolderId),
    );
    remove(selectedFolderIndex);
    handleCloseConfirmationDialog();
  };

  return (
    <>
      <div className="w-[255px] h-full overflow-y-auto bg-[#F4F4F4] border-l border-l-[#5F6368] p-1 flex flex-col gap-3">
        <div className="px-3 py-2 border-b border-b-lightDarkGray">
          <p className="m-0 text-xl font-light">תיקיות הצ’קליסטים</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto flex-col gap-3 px-2">
          <div>
            <div className="py-1 border-b border-b-tealBlue">
              <p className="m-0 text-base text-black80">משמרות</p>
            </div>

            <div className="pt-1 flex flex-col gap-2">
              {shifts?.map(shift => (
                <div
                  key={shift.id}
                  role="button"
                  tabIndex={0}
                  aria-selected={
                    selectedItem?.type === 'shift' &&
                    selectedItem.id === shift.id
                  }
                  onClick={() => onSelectShift(shift.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectShift(shift.id);
                    }
                  }}
                  className={`flex items-center justify-between ps-3 pe-2 py-1 rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px] transition-colors duration-200 hover:bg-softAqua 
                  ${selectedItem?.type === 'shift' && selectedItem.id === shift.id ? 'bg-softAqua' : ''}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5">
                      <img
                        src={SVG.clockIcon}
                        alt="clock"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-base text-black80">
                        {shift.name}
                      </span>
                      <span className="text-base text-black80">{`${shift.from} - ${shift.to}`}</span>
                    </div>
                  </div>
                  <span className="text-base text-black80">
                    {getChecklistCount('shift', shift.id)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="py-1 border-b border-b-tealBlue flex items-center justify-between">
              <p className="m-0 text-base text-black80">תיקיות</p>

              <button
                type="button"
                className="w-8 h-8 bg-white border border-gray70 rounded-lg flex items-center justify-center self-center shadow-sm"
                onClick={() => setShowAddFolderDialog(true)}>
                <img
                  src={SVG.addDarkBlueIcon}
                  alt="add"
                  className="w-[14px] h-[14px] object-contain"
                />
              </button>
            </div>

            <div className="pt-1 flex flex-col gap-2">
              {sortFoldersWithGeneralFirst(checklistFolders).map(
                ({folder, originalIndex: idx}) => (
                <div
                  key={folder.id}
                  role="button"
                  tabIndex={0}
                  aria-selected={
                    selectedItem?.type === 'folder' &&
                    selectedItem.id === folder.id
                  }
                  onClick={() => onSelectFolder(folder.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectFolder(folder.id);
                    }
                  }}
                  className={`flex items-center justify-between ps-3 pe-2 py-1 rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px] transition-colors duration-200 group hover:bg-grayLightBg}
                  ${selectedItem?.type === 'folder' && selectedItem.id === folder.id ? 'bg-grayLightBg' : ''}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-4">
                      <img
                        src={SVG.folderBlackIcon}
                        alt="clock"
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <span className="text-base text-black80">
                      {isGeneralChecklistFolder(folder)
                        ? GENERAL_FOLDER_NAME
                        : folder.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-base text-black80">
                      {getChecklistCount('folder', folder.id)}
                    </span>
                    {!isGeneralChecklistFolder(folder) && (
                      <button
                        type="button"
                        className="w-2.5 h-2.5 hidden group-hover:block"
                        onClick={e => {
                          e.stopPropagation();
                          handleRemoveFolder(idx, folder);
                        }}>
                        <img
                          src={SVG.closeDarkIcon}
                          className="w-full h-full object-contain"
                          alt="remove"
                        />
                      </button>
                    )}
                  </div>
                </div>
              ),
              )}
            </div>
          </div>
        </div>
      </div>

      <AddFolderDialog
        open={showAddFolderDialog}
        onClose={() => setShowAddFolderDialog(false)}
        onSave={handleAddFolder}
      />

      <AppConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        icon={
          <img src={SVG.deleteRedIcon} alt="" className="w-[32px] h-[32px]" />
        }
        title="מחיקה"
        subTitle={'בתיקיה יש קבצים, האם למחוק?'}
        confirmBtnText="כן"
        cancelBtnText="לא"
        onConfirm={handleRemoveFolderChecklists}
        cancelBtnTextClasses="!text-darkGray"
        bordered={false}
      />
    </>
  );
}

export default ChecklistSidebar;
